import { environment } from '../../environments/environment';
import { Component, OnInit, Input, ViewChild, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import * as fromAuthentication from './state/login.selectors';
import * as AuthenticationAction from './state/login.actions';
import * as appConfig from '../../app/app.constant';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, Subscription, from, of } from 'rxjs';

import { catchError, map, skipWhile, take } from 'rxjs/operators';
import { AuthenticationService, ErrorParseService, IExternalData, ILogin, LGEDataService } from 'src/commons';
import { AngularMaterialService } from 'src/commons/utilities/angular.material.service';

@Component({
  selector: 'login-screen',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class LoginScreenComponent implements OnInit, OnDestroy {
  @ViewChild('f1') einForm: any;
  @ViewChild('f2') extOrgInfoForm: any;
  @ViewChild('f3') extOrgContactInfoForm: any;
  @ViewChild('f4') extUserForm: any;
  @Input()
  pending$!: Observable<boolean>;
  pendingSubscription!: Subscription;
  pending!: boolean;
  @Input()
  error$!: Observable<string | null>;
  errorSubscription!: Subscription;
  isInternal = true;
  error!: string | null;
  externalLoginForm!: FormGroup;
  internalLoginForm!: FormGroup;
  appName = environment.APP_NAME;
  isExternalApp = environment.EXTERNAL_APP;
  showExternalLoginStepper: boolean = false;
  secondaryContactIncluded: boolean = false;
  isLinear: boolean = true;
  einFormGroup!: FormGroup;
  extOrgInfoFormGroup!: FormGroup;
  extOrgContactInfoFormGroup!: FormGroup;
  extUserFormGroup!: FormGroup;
  isReadOnly: boolean = false;
  isVerified: boolean = false;
  isEINValid: boolean = false;
  profileSubmittedSuccessfully: boolean = false;
  isEmailUnique: boolean = false;
  showEmailError: boolean = false;
  showGenericError: boolean = false;
  agreedToTerms: boolean = false;
  confirmEmailPattern!: RegExp;

  states = appConfig.SHARE_ARRAYS.states;
  taxExcemptList = appConfig.SHARE_ARRAYS.taxExemptList;
  forgotLink: string = "";

  constructor(
    private store: Store<fromAuthentication.State>,
    private ref: ChangeDetectorRef,
    private fb: FormBuilder,
    private ams: AngularMaterialService,
    private errorParse: ErrorParseService,
    private lgeDataService: LGEDataService,
    private router: Router,
    private route: ActivatedRoute,
    private ccAuth: AuthenticationService
  ) {
    this.selectState();
    this.createForms();
    if (environment.API_URL.includes('m.lke')) {
      this.isInternal = false;
      // this.forgotLink = `${environment.APP_URL}/CCMS/#/forgot-password`;
    }
  }

  ngOnInit() {
    if (!environment.API_URL.includes('m.lke')) {
      this.store.dispatch(new AuthenticationAction.Logout());
    } 
    else {
      
      this.authenticateSession().subscribe({
        next: (authed) => {
          if (!authed)
            this.store.dispatch(new AuthenticationAction.Logout());
          else 
            this.store.dispatch(new AuthenticationAction.AuthenticationRedirect());
        },
        error: (err) => {
          this.store.dispatch(new AuthenticationAction.Logout());
        }
      })
    }
    this.startSubscriptions();
  }

  private authenticateSession(): Observable<any> {
    return this.ccAuth.validateSession()
    // return from(this.authenticateSessionPromise()).pipe(
    //   map((data: any) => data)
    // );
  }

  // private authenticateSessionPromise(): Observable<any> {
  //   return this.ccAuth.validateSession().pipe(
  //     map(() => of('good')),
  //     catchError(err => of('bad'))
  //   )
  //   // .then(data => {
  //   //   resolve('good');
  //   // })
  //   // .catch(data => {
  //   //   resolve('bad');
  //   // });
  // }

  setConfirmEmailPattern(): void {
    this.confirmEmailPattern = new RegExp(`^` + this.extUserFormGroup.value['email'] + `$`);
    this.extUserFormGroup.controls['confirmEmail'].setValidators([Validators.required, Validators.maxLength(254), Validators.pattern(this.confirmEmailPattern)]);
  }

  setConfirmEmailPatternClear(): void {
    this.setConfirmEmailPattern();
    this.isEmailUnique = false;
    this.showEmailError = false;
    this.showGenericError = false;
    (<FormControl>this.extUserFormGroup.controls['confirmEmail']).setValue('');
  }

  private selectState(): void {
    this.pending$ = this.store.select(fromAuthentication.getLoginScreenPending);
    this.error$ = this.store.select(fromAuthentication.getLoginScreenError).pipe(skipWhile((error) => error === undefined || error === null || error === ''));
  }

  private createForms(): void {
    this.internalLoginForm = this.fb.group({
      userID: ['', [Validators.required, Validators.maxLength(7)]],
      password: ['', [Validators.required, Validators.minLength(3)]]
    });
    this.externalLoginForm = this.fb.group({
      email: ['', [Validators.required, Validators.minLength(3), Validators.pattern(new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/))]],
      password: ['', [Validators.required, Validators.minLength(3)]]
    });
    this.einFormGroup = this.fb.group({
      EINCtrl: ['', [Validators.required, Validators.maxLength(10), Validators.pattern(new RegExp(/^\d{2}-\d{7}/))]]
    });
    this.extOrgInfoFormGroup = this.fb.group({
      orgInfoExtId: [null],
      name: ['', [Validators.required]],
      EIN: ['', [Validators.required, Validators.maxLength(10), Validators.pattern(new RegExp(/^\d{2}-\d{7}/))]],
      address1: ['', [Validators.required, Validators.maxLength(35)]],
      address2: ['', [Validators.maxLength(35)]],
      city: ['', [Validators.required, Validators.maxLength(50)]],
      state: ['', Validators.required],
      zipCode: ['', [Validators.required, Validators.pattern(new RegExp(/^\d{5}$|^\d{9}$/))]],
      webAddress: [''],
      subsection_501: ['']
    });
    this.extOrgContactInfoFormGroup = this.fb.group({
      orgContactInfoExtId: [null],
      title: [''],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: ['', [Validators.required, Validators.pattern(new RegExp(/^\d{3}-\d{3}-\d{4}/))]],
      email: ['', [Validators.required, Validators.maxLength(254), Validators.pattern(new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/))]],

      secondaryOrgContactInfoExtId: [null],
      secondaryTitle: [''],
      secondaryFirstName: [''],
      secondaryLastName: [''],
      secondaryPhone: ['', [Validators.pattern(new RegExp(/^\d{3}-\d{3}-\d{4}/))]],
      secondaryEmail: ['', [Validators.maxLength(254), Validators.pattern(new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/))]]
    });
    this.extUserFormGroup = this.fb.group({
      externalUserId: [null],
      orgInfoExtId: [null],
      orgContactInfoExtId: [null],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.maxLength(254), Validators.pattern(new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/))]],
      confirmEmail: ['', [Validators.required, Validators.maxLength(254)]]
    });
    this.ams.closeDialog();
    this.ref.markForCheck();
  }

  tempBypassLogin() {
    this.router.navigate(['/home'], { relativeTo: this.route });
  }

  verifyEIN() {
    let entry = this.einFormGroup.value;
    if (entry && entry.EINCtrl) {
      this.ams.showLoader('Verifying...');

      this.lgeDataService.externalCharityCheck(entry.EINCtrl).subscribe({
        next: result => {
          this.ams.closeDialog();
          if (!JSON.parse(result).errorExists) {
            let data = JSON.parse(result).data;
            if (data !== 'invalid' && data !== 'Expired') {
              data = JSON.parse(data);
              this.isEINValid = true;
              if (data && data.data) {
                let dataRes = data.data;
                (<FormControl>this.extOrgInfoFormGroup.controls['webAddress']).setValue('');
                (<FormControl>this.extOrgInfoFormGroup.controls['EIN']).setValue(dataRes.ein);
                (<FormControl>this.extOrgInfoFormGroup.controls['name']).setValue(dataRes.organization_name.toUpperCase());
                (<FormControl>this.extOrgInfoFormGroup.controls['address1']).setValue(dataRes.address_line1);
                (<FormControl>this.extOrgInfoFormGroup.controls['address2']).setValue(dataRes.address_line2);
                (<FormControl>this.extOrgInfoFormGroup.controls['city']).setValue(dataRes.city);
                (<FormControl>this.extOrgInfoFormGroup.controls['state']).setValue(dataRes.state);
                (<FormControl>this.extOrgInfoFormGroup.controls['zipCode']).setValue(dataRes.zip);
                (<FormControl>this.extOrgInfoFormGroup.controls['subsection_501']).setValue(dataRes.subsection_501 || dataRes.bmf_subsection || '');
              } else if (data) {
                let dataRes = data;
                (<FormControl>this.extOrgInfoFormGroup.controls['webAddress']).setValue('');
                (<FormControl>this.extOrgInfoFormGroup.controls['EIN']).setValue(dataRes.ein);
                (<FormControl>this.extOrgInfoFormGroup.controls['name']).setValue(dataRes.organization_name.toUpperCase());
                (<FormControl>this.extOrgInfoFormGroup.controls['address1']).setValue(dataRes.address_line1);
                (<FormControl>this.extOrgInfoFormGroup.controls['address2']).setValue(dataRes.address_line2);
                (<FormControl>this.extOrgInfoFormGroup.controls['city']).setValue(dataRes.city);
                (<FormControl>this.extOrgInfoFormGroup.controls['state']).setValue(dataRes.state);
                (<FormControl>this.extOrgInfoFormGroup.controls['zipCode']).setValue(dataRes.zip);
                (<FormControl>this.extOrgInfoFormGroup.controls['subsection_501']).setValue(dataRes.subsection_501 || dataRes.bmf_subsection || '');
              }
              this.isVerified = true;
              this.ref.markForCheck();
            } else {
              this.isVerified = true;
              this.isEINValid = false;
              this.extOrgInfoFormGroup.get('EIN')?.setValue(entry.EINCtrl);
              this.ams.showAlert('The exemption status of the Tax ID/EIN entered was not validated.  Please press OK to proceed.', '');
              this.ref.markForCheck();
            }
          } else {
            throw new Error(JSON.parse(result).message);
          }
        },
        error: (error: any) => {
          {
            this.ams.closeDialog();
            this.ams.showAlert('Error', `Please contact contributions@lge-ku.com for assistance. \n ${JSON.stringify((error))}}`);
            this.ref.markForCheck();
            return of(Error(error.message))
          }
        }
      })
    }
  }

  verifyEmailUnique(): void {
    this.ams.showLoader('Verifying...');
    this.lgeDataService.externalEmailCheckMVC(this.extUserFormGroup.value['email']).
      subscribe((response) => {
        let matchCount;
        if (response) {
          matchCount = Number(response);
        }
        this.ams.closeDialog();
        if (matchCount && matchCount > 0) {
          this.isEmailUnique = false;
          this.showEmailError = true;
          this.showGenericError = false;
        } else {
          this.isEmailUnique = true
          this.showEmailError = false;
          this.showGenericError = false;
        }
        this.ref.markForCheck();
      })
  }

  populateFromContactInfo(event: { checked: boolean; }) {
    if (event && event.checked == true) {
      (<FormControl>this.extUserFormGroup.controls['firstName']).setValue(this.extOrgContactInfoFormGroup.value['firstName']);
      (<FormControl>this.extUserFormGroup.controls['lastName']).setValue(this.extOrgContactInfoFormGroup.value['lastName']);
      (<FormControl>this.extUserFormGroup.controls['email']).setValue(this.extOrgContactInfoFormGroup.value['email']);
    } else {
      (<FormControl>this.extUserFormGroup.controls['firstName']).setValue('');
      (<FormControl>this.extUserFormGroup.controls['lastName']).setValue('');
      (<FormControl>this.extUserFormGroup.controls['email']).setValue('');

    }
    (<FormControl>this.extUserFormGroup.controls['confirmEmail']).setValue('');
  }

  toggleAgreementToTerms(event: any) {
    if (event && event.checked == true) {
      this.agreedToTerms = true;
    } else {
      this.agreedToTerms = false;
    }
  }

  addAdditionalContact() {
    this.secondaryContactIncluded = true;
  }

  removeAdditionalContact() {
    this.secondaryContactIncluded = false;
  }

  getTaxExemptStatus(value: any) {
    if (value === '')
      return 'Other';
    else if (value === '')
      return 'Other';
    else if (isNaN(value) || +value === 0) {
      return value;
    } else {
      return `501(c)(${+value})`;
    }
  }

  submitProfileForms() {
    let externalData: IExternalData = {
      orgInfoExt: this.extOrgInfoFormGroup.value,
      orgContactInfoExt: this.extOrgContactInfoFormGroup.value,
      externalUser: Object.assign({}, this.extUserFormGroup.value, { accessStatus: 'pending', isVerified: this.isEINValid })
    };
    this.ams.showLoader('Submitting...');
    this.lgeDataService.externalSubmitAccessRequestMVC(externalData).subscribe({
      next: () => {
        this.ams.closeDialog();
        this.profileSubmittedSuccessfully = true;
        this.ref.markForCheck();
      },
      error: (error: any) => {
        this.ams.closeDialog();
        this.ams.showAlert('Error', `Please contact contributions@lge-ku.com for assistance. \n ${error.message}`);
        this.ref.markForCheck();
        return of(Error(error))
      }
    })
  }

  login(): void {
    let login: ILogin;
    if(this.isInternal)
    login = { username: this.internalLoginForm.value['userID'], password: this.internalLoginForm.value['password'] }
      else
    login = { username: this.externalLoginForm.value['email'], password: this.externalLoginForm.value['password'] }
    this.store.dispatch(new AuthenticationAction.Login(login));
  }

  openExternalRegistration() {
    this.showExternalLoginStepper = true;
    this.isVerified = false;
    this.ams.showLoader('');
    this.ref.markForCheck();
    this.createForms();
  }

  closeExternalAccessForm() {
    if (!this.profileSubmittedSuccessfully) {
      this.ams.showConfirm('Are you sure want to cancel the register?', '');
      this.ams.confirm.afterClosed().subscribe(result => {
        if (result === 'yes') {
          this.resetSignUp();
        }
      })
    } else
      this.resetSignUp();
  }

  resetSignUp() {
    this.ams.showLoader('');
    this.ref.markForCheck();
    this.showExternalLoginStepper = false;
    this.createForms();
    this.profileSubmittedSuccessfully = false;
    this.isLinear = true;
    this.secondaryContactIncluded = false;
    this.isEmailUnique = false;
    this.showEmailError = false;
    this.agreedToTerms = false;
    this.showGenericError = false;
    this.isVerified = false;
    this.isEINValid = false;
  }

  private startSubscriptions(): void {
    this.pendingSubscription = this.pending$
      .subscribe((data: boolean) => {

        this.pending = data;
        this.displayLoader(this.pending);
        this.ref.markForCheck();
      });
    this.errorSubscription = this.error$
      .subscribe((data: string | null) => {
        this.error = data;
        this.ref.markForCheck();
      });
  }

  private displayLoader(pending: boolean): void {
    if (pending) {
      this.externalLoginForm.disable();
      this.ams.showLoader("Authenticating");
    } else {
      this.externalLoginForm.enable();
      this.ams.closeDialog().then(() => {
        if (this.error !== null && this.error !== undefined && this.error !== '') {
          this.displayError(this.error);
        }
      });
    }
  }

  private displayError(error: any): void {
    let errorMessage;
    if (this.errorParse.foundKeyWord(error, "CORPCOMMONWS")) {
      errorMessage = this.errorParse.translateCCError(error);
    } else {
      errorMessage = error instanceof Object ? error.message : error;
    }
    this.ams.showAlert("Login Error", errorMessage);
    this.externalLoginForm.controls['password'].reset();
    this.ams.alert.afterClosed().subscribe((_result: any) => {
      this.store.dispatch(new AuthenticationAction.LoginErrorReset());
    });
  }

  private endSubscriptions(): void {
    this.pendingSubscription.unsubscribe();
    this.errorSubscription.unsubscribe();
  }

  ngOnDestroy(): void {
    this.endSubscriptions();
  }
}